import { Loader2 } from "lucide-react";
import { Card } from "@/shared/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { cn } from "@/shared/lib/utils";
import type { PaginationSlot } from "./pagination";

interface SectionPaginationProps {
  isGroupedSearch: boolean;
  countSummary: string;
  loading: boolean;
  pageSize: number;
  onPageSizeChange: (value: string) => void;
  currentPage: number;
  totalPages: number;
  paginationSlots: PaginationSlot[];
  onPageChange: (page: number) => void;
}

export function SectionPagination({
  isGroupedSearch,
  countSummary,
  loading,
  pageSize,
  onPageSizeChange,
  currentPage,
  totalPages,
  paginationSlots,
  onPageChange,
}: SectionPaginationProps) {
  return (
    <div className="flex-shrink-0 pt-4">
      <Card className="glass-effect border-border/50 p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{countSummary}</span>
            {loading && (
              <span className="inline-flex items-center gap-1 text-primary">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Updating
              </span>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 md:min-w-[420px]">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{isGroupedSearch ? "Rows per group" : "Rows"}</span>
              <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
                <SelectTrigger className="h-8 w-[86px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isGroupedSearch && (
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                      className={cn(
                        "cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors",
                        currentPage === 1 && "pointer-events-none opacity-50"
                      )}
                    />
                  </PaginationItem>

                  {paginationSlots.map((slot, index) => {
                    if (slot.type === "ellipsis") {
                      return (
                        <PaginationItem key={`${slot.key}-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }

                    return (
                      <PaginationItem key={slot.page}>
                        <PaginationLink
                          onClick={() => onPageChange(slot.page)}
                          isActive={currentPage === slot.page}
                          className={cn(
                            "cursor-pointer transition-all duration-300",
                            currentPage === slot.page && "bg-gradient-primary text-primary-foreground shadow-elegant"
                          )}
                        >
                          {slot.page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                      className={cn(
                        "cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors",
                        currentPage === totalPages && "pointer-events-none opacity-50"
                      )}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
